import Link, { LinkProps } from 'next/link'
import React from 'react'

import Icon from '../../atoms/icons/icon/Icon'
import { cn } from '../../../shadcn/utils'

type BreadcrumbProps = {
  children: React.ReactNode
  className?: string
  olClassName?: string
}

function BreadcrumbComponent({
  children,
  className,
  olClassName,
}: BreadcrumbProps) {
  const childCount = React.Children.count(children)
  return (
    <nav aria-label='breadcrumb' className={className}>
      <ol className={cn('flex flex-wrap gap-2', olClassName)}>
        {React.Children.map(children, (child, i) => {
          if (!React.isValidElement<BreadcrumbItemProps>(child)) {
            throw new Error(
              'Breadcrumb only accepts Breadcrumb.Item as children',
            )
          }
          return React.cloneElement(child, {
            ...child.props,
            current: i === childCount - 1,
          })
        })}
      </ol>
    </nav>
  )
}

type BreadcrumbItemProps = React.AnchorHTMLAttributes<HTMLAnchorElement> &
  Partial<LinkProps> & {
    current?: boolean
    disabled?: boolean
    onClick?: () => void
  }

function BreadcrumbItem({
  current = false,
  disabled = false,
  className,
  onClick,
  children,
  href,
}: BreadcrumbItemProps) {
  const isInteractive = !disabled && !current
  const classes = cn(
    'font-normal antialiased',
    current ? 'text-black' : 'text-muted-foreground',
    className,
  )
  const interactiveClasses =
    'cursor-pointer underline-offset-4 hover:underline focus:underline focus:outline-none'

  const linkComponent = (() => {
    if (href && isInteractive) {
      return (
        <Link
          href={href}
          className={cn(classes, interactiveClasses, className)}
        >
          {children}
        </Link>
      )
    }

    if (onClick && isInteractive) {
      return (
        <div
          role='button'
          className={cn(classes, interactiveClasses, className)}
          onClick={onClick}
        >
          {children}
        </div>
      )
    }

    return (
      <a
        aria-disabled='true'
        aria-current='page'
        className={cn(classes, 'cursor-default', className)}
      >
        {children}
      </a>
    )
  })()

  return (
    <li className='flex items-center gap-2'>
      {linkComponent}
      {!current && (
        <Icon
          name='arrow-right'
          aria-hidden={true}
          className='text-muted-foreground'
          size={16}
        />
      )}
    </li>
  )
}

type BreadcrumbComponent = React.FC<BreadcrumbProps> & {
  Item: React.FC<BreadcrumbItemProps>
}

/**
 * @example
 * ```tsx
 * <Breadcrumb>
 *   <Breadcrumb.Item href='/' disabled>Home</Breadcrumb.Item>
 *   <Breadcrumb.Item href='/about'>About</Breadcrumb.Item>
 *   <Breadcrumb.Item href='/contact'>Contact</Breadcrumb.Item>
 * </Breadcrumb>
 */
const Breadcrumb = BreadcrumbComponent as BreadcrumbComponent
Breadcrumb.Item = BreadcrumbItem

Breadcrumb.displayName = 'Breadcrumb'
Breadcrumb.Item.displayName = 'Breadcrumb.Item'

export { Breadcrumb }
